import { useEffect, useState } from "react";
import { X, Save, Loader2, Crosshair, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FORMS } from "@/components/wifi/wifiFields";

const inp = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-cyan-500";

function CalcPreview({ kind, calc }) {
  if (!calc) return null;
  const regs = calc.regulatory || [];
  const rows = kind === "links"
    ? [["Odległość", calc.distance_m != null ? `${calc.distance_m} m` : "—"], ["Azymut A→B", calc.azimuth_ab != null ? `${calc.azimuth_ab}°` : "—"],
       ["Azymut B→A", calc.azimuth_ba != null ? `${calc.azimuth_ba}°` : "—"], ["Różnica wysokości", calc.height_diff_m != null ? `${calc.height_diff_m} m` : "—"],
       ["Kąt anteny A", calc.tilt_a_deg != null ? `${calc.tilt_a_deg}°` : "—"], ["Kąt anteny B", calc.tilt_b_deg != null ? `${calc.tilt_b_deg}°` : "—"],
       ["Promień 1. strefy Fresnela (środek)", calc.fresnel_r1_mid_m != null ? `${calc.fresnel_r1_mid_m} m` : "—"],
       ["Tłumienie FSPL", calc.budget ? `${calc.budget.fspl_db} dB` : "—"], ["Przewidywany RSSI", calc.budget ? `${calc.budget.rssi_dbm} dBm` : "—"],
       ["SNR", calc.budget ? `${calc.budget.snr_db} dB` : "—"], ["Przepustowość", calc.budget ? `~${calc.budget.throughput_mbps} Mb/s` : "—"],
       ["Opóźnienie", calc.budget ? `${calc.budget.latency_ms} ms` : "—"]]
    : [["EIRP", calc.eirp_dbm != null ? `${calc.eirp_dbm} dBm` : "—"],
       ["Oś wiązki na gruncie", calc.footprint?.main_m != null ? `${calc.footprint.main_m} m` : "—"],
       ["Pokrycie (rzut na grunt)", calc.footprint ? `${calc.footprint.inner_m}–${calc.footprint.outer_m} m` : "—"]];
  return (
    <div className="rounded border border-cyan-800/60 bg-cyan-950/30 p-2.5 text-[11px]" data-testid="wifi-calc-preview">
      <div className="font-semibold text-cyan-300 mb-1">Obliczenia (szacunkowe)</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {rows.map(([l, v]) => <div key={l} className="flex justify-between gap-2"><span className="text-slate-400">{l}</span><span className="font-mono text-slate-200">{v}</span></div>)}
      </div>
      {calc.footprint?.limited_by_tilt && <div className="text-amber-300 mt-1">Downtilt skraca zasięg wiązki na gruncie.</div>}
      {kind === "links" && <div className="text-slate-500 mt-1">{calc.estimated_note}</div>}
      {regs.map((r, i) => (
        <div key={i} className={`flex gap-1 mt-1 ${r.level === "error" ? "text-red-400" : "text-slate-400"}`} data-testid={`wifi-reg-${r.level}`}>
          {r.level === "error" ? <AlertTriangle size={12} /> : <Info size={12} />} {r.msg}
        </div>
      ))}
    </div>
  );
}

export default function WifiForm({ kind, initial, refs, mapCenter, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = { ...(initial || {}) };
    if (f.pos_from_site) { f.lat = ""; f.lng = ""; }
    return f;
  });
  const [busy, setBusy] = useState(false);
  const [calc, setCalc] = useState(initial?.calc || null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (kind !== "devices" && kind !== "links") return undefined;
    const tm = setTimeout(() => {
      const { calc: _c, ...body } = form;
      api.post(`/wifi/calc/${kind === "links" ? "link" : "device"}`, body).then((r) => setCalc(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(tm);
  }, [form, kind]);

  const save = async () => {
    setBusy(true);
    try {
      const { calc: _c, counts: _n, site_name: _s, pos_from_site: _p, ...body } = form;
      const { data } = initial?.id ? await api.put(`/wifi/${kind}/${initial.id}`, body) : await api.post(`/wifi/${kind}`, body);
      toast.success("Zapisano"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const field = (f) => {
    const v = form[f.k] ?? "";
    const tid = `wifi-field-${f.k}`;
    if (f.opts) return <select value={v} onChange={(e) => set(f.k, e.target.value)} className={inp} data-testid={tid}><option value="">—</option>{f.opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
    if (f.ref) {
      const list = (refs[f.ref] || []).filter((o) => o.id !== initial?.id);
      return <select value={v} onChange={(e) => set(f.k, e.target.value)} className={inp} data-testid={tid}><option value="">—</option>{list.map((o) => <option key={o.id} value={o.id}>{o.name || `${o.manufacturer} ${o.model}`}</option>)}</select>;
    }
    if (f.type === "textarea") return <textarea value={v} onChange={(e) => set(f.k, e.target.value)} rows={2} className={inp} data-testid={tid} />;
    if (f.type === "color") return <input type="color" value={v || "#A855F7"} onChange={(e) => set(f.k, e.target.value)} className="w-12 h-8 rounded bg-transparent border border-slate-600" data-testid={tid} />;
    return <input type={f.type === "date" ? "date" : "text"} inputMode={f.type === "number" ? "decimal" : undefined} value={v} onChange={(e) => set(f.k, e.target.value)} className={inp} data-testid={tid} />;
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-3" data-testid={`wifi-form-${kind}`}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <h3 className="font-semibold text-sm">{initial?.id ? "Edycja" : "Nowy obiekt"}</h3>
          <button onClick={onClose} data-testid="wifi-form-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {FORMS[kind].map((grp) => (
            <div key={grp.title}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{grp.title}</div>
                {grp.fields.some((f) => f.geo) && mapCenter && (
                  <button onClick={() => { set("lat", Number(mapCenter[0].toFixed(6))); set("lng", Number(mapCenter[1].toFixed(6))); }}
                    data-testid="wifi-use-map-center" className="text-[10px] text-cyan-400 flex items-center gap-1 hover:underline"><Crosshair size={11} /> środek mapy</button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {grp.fields.map((f) => (
                  <label key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <span className="text-[11px] text-slate-400">{f.l}{f.req ? " *" : ""}</span>{field(f)}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <CalcPreview kind={kind} calc={calc} />
        </div>
        <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded hover:bg-slate-800">Anuluj</button>
          <button onClick={save} disabled={busy} data-testid="wifi-form-save" className="px-3 py-1.5 text-xs rounded bg-cyan-600 hover:bg-cyan-500 flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz
          </button>
        </div>
      </div>
    </div>
  );
}
